import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/services/api';
import { useAdminCabinetUrl } from '@/context/AdminCabinetUrlContext';
import { normalizeSupportHtml } from '@/utils/supportHtml';
import { Label } from '@/components/ui/label';
import { Loader2, MessageSquare, RefreshCw, Send, Trash2 } from 'lucide-react';

type SupportChat = {
  id: number;
  user_id: string;
  user_name: string;
  user_email?: string | null;
  store_id?: string | null;
  store_name?: string | null;
  last_message_at: string;
  last_message_preview: string;
};

type SupportMessage = {
  id: number;
  role: string;
  content: string;
  needs_manager: boolean;
  created_at: string;
};

function formatDate(s: string) {
  return new Date(s).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UserChats() {
  const { url: adminCabinetUrl, buildShopUrl } = useAdminCabinetUrl();
  const [chats, setChats] = useState<SupportChat[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [replyInput, setReplyInput] = useState('');
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const loadChats = async () => {
    setIsLoadingChats(true);
    try {
      const { chats: list } = await api.getSupportChats();
      setChats(list);
    } catch (err) {
      console.error('Failed to load chats:', err);
      setChats([]);
    } finally {
      setIsLoadingChats(false);
    }
  };

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      return;
    }
    setIsLoadingMessages(true);
    api
      .getSupportChatMessages(selectedChatId)
      .then(({ messages: list }) => setMessages(list))
      .catch(() => setMessages([]))
      .finally(() => setIsLoadingMessages(false));
  }, [selectedChatId]);

  const handleSendReply = async () => {
    const text = replyInput.trim();
    if (!text || !selectedChatId || isSending) return;
    setIsSending(true);
    try {
      await api.replyToSupportChat(selectedChatId, text);
      setReplyInput('');
      const { messages: list } = await api.getSupportChatMessages(selectedChatId);
      setMessages(list);
      loadChats();
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setIsSending(false);
    }
  };

  const selectedChat = chats.find((c) => c.id === selectedChatId);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const DELETE_CONFIRM_WORD = 'Удалить';

  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setDeleteDialogOpen(false);
    setDeleteConfirmInput('');
    setDeleteError(null);
  };

  const handleDeleteChat = async () => {
    if (!selectedChatId) return;
    if (deleteConfirmInput.trim() !== DELETE_CONFIRM_WORD) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteSupportChat(selectedChatId);
      setSelectedChatId(null);
      setMessages([]);
      setDeleteDialogOpen(false);
      setDeleteConfirmInput('');
      await loadChats();
    } catch (err) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setDeleteError(msg || 'Не удалось удалить чат');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto px-6 py-6 h-full flex flex-col">
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Чаты пользователей
          </CardTitle>
          <Button variant="outline" size="sm" onClick={loadChats} disabled={isLoadingChats}>
            <RefreshCw className={`w-4 h-4 ${isLoadingChats ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="flex-1 flex gap-4 min-h-0 p-0">
          <div className="w-72 shrink-0 border-r flex flex-col">
            <div className="p-2 border-b text-sm text-muted-foreground">
              Выберите чат для просмотра и ответа
            </div>
            <div className="flex-1 overflow-y-auto">
              {isLoadingChats ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : chats.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">
                  Нет чатов. Чаты появятся, когда пользователи напишут в поддержку (Wiki).
                </p>
              ) : (
                <div className="divide-y">
                  {chats.map((chat) => (
                    <button
                      key={chat.id}
                      type="button"
                      onClick={() => setSelectedChatId(chat.id)}
                      className={`w-full p-3 text-left hover:bg-muted/50 transition-colors ${
                        selectedChatId === chat.id ? 'bg-primary/10 border-l-2 border-primary' : ''
                      }`}
                    >
                      <p className="font-medium truncate">{chat.user_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{chat.user_id}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {chat.last_message_preview || '—'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(chat.last_message_at)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-w-0">
            {selectedChatId ? (
              <>
                <div className="p-2 border-b shrink-0 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                  <p className="font-medium">{selectedChat?.user_name}</p>
                  <div className="text-sm text-muted-foreground space-y-0.5 mt-1">
                    {selectedChat?.user_email && (
                      <p>Email: {selectedChat.user_email}</p>
                    )}
                    {selectedChat?.store_name && (
                      <p>
                        Магазин:{' '}
                        {adminCabinetUrl && selectedChat.store_id ? (
                          <a
                            href={buildShopUrl(selectedChat.store_id, '/general') || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {selectedChat.store_name}
                          </a>
                        ) : (
                          selectedChat.store_name
                        )}
                      </p>
                    )}
                    <p>ID: {selectedChat?.user_id}</p>
                  </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => { setDeleteDialogOpen(true); setDeleteConfirmInput(''); setDeleteError(null); }}
                    title="Удалить чат"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Удалить чат
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {isLoadingMessages ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : msg.role === 'admin'
                                ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700'
                                : 'bg-muted'
                          }`}
                        >
                          {(msg.role === 'assistant' || msg.role === 'admin') ? (
                            <div
                              className="whitespace-pre-wrap break-words"
                              dangerouslySetInnerHTML={{ __html: normalizeSupportHtml(msg.content) }}
                            />
                          ) : (
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          )}
                          {msg.role === 'admin' && (
                            <p className="mt-1 text-xs text-muted-foreground">Администратор</p>
                          )}
                          {msg.role === 'assistant' && msg.needs_manager && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Обращение передано менеджеру
                            </p>
                          )}
                          <p className="mt-1 text-xs text-muted-foreground opacity-90">
                            {formatDate(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-4 border-t flex gap-2 shrink-0">
                  <Input
                    placeholder="Введите ответ..."
                    value={replyInput}
                    onChange={(e) => setReplyInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
                    disabled={isSending}
                    className="flex-1"
                  />
                  <Button
                    size="icon"
                    onClick={handleSendReply}
                    disabled={!replyInput.trim() || isSending}
                    aria-label="Отправить"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                Выберите чат слева
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {deleteDialogOpen && selectedChat && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          onClick={closeDeleteDialog}
        >
          <div
            className="bg-background border rounded-lg shadow-lg max-w-md w-full mx-4 p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Подтверждение удаления чата"
          >
            <h3 className="text-lg font-semibold">Удалить чат с «{selectedChat.user_name}»?</h3>
            <p className="text-sm text-muted-foreground">
              Будут удалены сам чат и все его сообщения. Это действие необратимо.
            </p>
            <div className="space-y-2">
              <Label htmlFor="delete-chat-confirm-input">
                Для подтверждения введите <code className="px-1 py-0.5 rounded bg-muted">{DELETE_CONFIRM_WORD}</code>
              </Label>
              <Input
                id="delete-chat-confirm-input"
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                autoFocus
                disabled={isDeleting}
              />
            </div>
            {deleteError && (
              <p className="text-sm text-destructive">{deleteError}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeDeleteDialog} disabled={isDeleting}>
                Отмена
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteChat}
                disabled={isDeleting || deleteConfirmInput.trim() !== DELETE_CONFIRM_WORD}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Удаление...
                  </>
                ) : (
                  'Удалить'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
